import { getOwner } from '@ember/application';
import EmberObject, { set } from '@ember/object';
import { addObserver, removeObserver } from '@ember/object/observers'
import { once } from '@ember/runloop';
import Mixin from '@ember/object/mixin';

export default Mixin.create({

  //*******************************
  /////////////////////////////////
  // State Manager Mixin
  //
  // This Mixin is inteded to assist with state tracking for various
  // UX elements on the page.  Use this in conjunction with a route-name-based
  // stateService, to allow for high level state monitoring on any configured
  // Components.
  /////////////////////////////////
  //*******************************

  // This mixin expects the following properties to be passed in/set on the implementing object

  /////////////////////////////////
  // stateServiceName (required)
  //
  // * Identifies the file name of the service
  // * Ex: 'states/message-center'
  /////////////////////////////////

  /////////////////////////////////
  // stateServicePath (required)
  //
  // * Identifies the 'get()' path of the object you'd like to update
  // * Ex: 'modals.declineOnArchive'
  // *
  // * In this case, your stateService might look something like this...
  // * {
  // *   subNav: {
  // *     isTransitioning: undefined
  // *   },
  // *   modals: {
  // *     declineOnArchive: {
  // *       awaitingResponse: undefined
  // *     },
  // *     createProject: {
  // *       awaitingResponse: undefined
  // *     }
  // *   }
  // * }
  /////////////////////////////////

  /////////////////////////////////
  // stateServiceProperties (required)
  //
  // * This is a string that identifies the 'local' and 'service' property bindings.  Multiple
  // * properties should be comma-separated with a format of 'servicePropertyName:localPropertyName'
  // * to identify the binding. If the local and state properties share the same name,
  // * you can just type the name once
  // *
  // * Ex: 'inputHasText, inputActive, active:inputHasTextOrIsActive'
  /////////////////////////////////

  /////////////////////////////////
  // stateServiceUniqueBy
  //
  // * This property allows for several of the same Component to be tracked individually.
  // * It should be the name of a local property which will guarantee uniqueness
  // *
  // * Ex: 'thread.id'
  // *
  // * In this case, any of the stateServiceProperties will have their name(s) appended with
  // * the result of this.get('thread.id') in the implementing object (Component).  So instead
  // * of looking for stateService.stateServicePath.property in your template, you'll instead
  // * be looking for 'stateService.stateServicePath.property' + this.get('thread.id')
  /////////////////////////////////


  _MIX_updateStateRaw: function(serviceProperty, localProperty, stateServicePath, stateService, value) {

    // If not provided, look locally for pre-set stateService/statePath values
    stateServicePath = (typeof stateServicePath === 'undefined') ? this.get('stateServicePath') : stateServicePath;
    stateService = (typeof stateService === 'undefined') ? this.get('stateService') : stateService;

    // Initialize local property if not provided
    localProperty = (typeof localProperty === 'string') ? localProperty : serviceProperty;

    // Initialize value if not provided
    value = arguments.length === 5 ? value : this.get(localProperty);

    stateService.set(`${stateServicePath}.${serviceProperty}`, value);
  },

  _MIX_updateState: function(localProperty, value) {
    let ssParsedProperties = this.get('_MIX_ssParsedProperties');

    let serviceProperty = ssParsedProperties.get(localProperty);

    this._MIX_updateStateRaw(serviceProperty, localProperty, undefined, undefined, value);
  },

  init() {
    this._super(...arguments);

      let stateServiceName = this.get('stateServiceName');
      let stateServicePath = this.get('stateServicePath');
      let stateServiceProperties = this.get('stateServiceProperties');
      let stateServiceUniqueBy = this.get('stateServiceUniqueBy');

      if(!stateServiceName || !stateServicePath || !stateServiceProperties) {
        return;
      }

      // Initialize the stateService as an injected property
      let stateService = getOwner(this).lookup(`service:${stateServiceName}`);
      this.set('stateService', stateService);

      // Parse the stateServiceProperties and create an object to store the key/value pairs
      let ssParsedProperties = EmberObject.create();
      let properties = stateServiceProperties.split(',');

      this._cleanupObservers = properties.map((property) => {
        let parsedProperty = property.trim().split(':');

        let serviceProperty;
        let localProperty;

        if(parsedProperty.length > 1) {
          serviceProperty = parsedProperty[0];
          localProperty = parsedProperty[1];
        } else {
          serviceProperty = localProperty = parsedProperty[0];
        }

        if(stateServiceUniqueBy) {
          // Initialize UniqeBy property on the object
          serviceProperty = stateServiceUniqueBy ? serviceProperty + this.get(stateServiceUniqueBy) : serviceProperty;
          set(this.get(`stateService.${stateServicePath}`), serviceProperty, undefined);
        }

        ssParsedProperties.set(localProperty, serviceProperty);

        const observer = () => {
          once(this, () => {
            this._MIX_updateStateRaw(serviceProperty, localProperty);
          });
        };

        addObserver(this, localProperty, this, observer);

        return () => {
          removeObserver(this, localProperty, this, observer);
        };
      });

      // Set the stored parsed properties as a local property
      this.set('_MIX_ssParsedProperties', ssParsedProperties);
  },

  willDestroy() {
    this._super(...arguments);
    this._cleanupObservers?.forEach((f) => f());
  },

});
